import type { QuoteItem } from '../../model/pages';
import { Stars, type SectionKit } from './kit';
import VideoPoster from './VideoPoster';
import { normalizeSectionDesign } from '../../model/section-design';
import { sectionGridProps } from '../section-design';

/** Review wall: many short testimonials. Default = an even 3-up grid of
 *  cards; 'wall' packs them into masonry columns (uneven lengths stack
 *  tightly); 'list' = full-width rows with the photo on the left (the
 *  long-form letter pattern). A card may lead with a screenshot or a video. */
export default function TestimonialsBlock({ k }: { k: SectionKit }) {
  const { section, s, brand, onBrand, btnBg, onBtn, tz, txt, withHead, ph, editing } = k;
  const all = section.quotes ?? [];
  const quotes = all.map((q, i) => ({ q, i })).filter(({ q }) => q.quote || q.name);
  if (!quotes.length) return withHead(ph('Reviews', 'Add reviews in the panel'));
  const patch = (i: number, p: Partial<QuoteItem>) => { const a = [...all]; a[i] = { ...a[i], ...p }; return { quotes: a }; };
  const card = s.quoteCard ?? s.card;
  const avatar = (q: QuoteItem, size: string) => q.photo_url
    ? <img src={q.photo_url} alt="" loading="lazy" decoding="async" className={`${size} flex-none rounded-full object-cover`} />
    : q.name ? <span aria-hidden className={`grid ${size} flex-none place-items-center rounded-full text-sm font-bold`} style={{ background: brand, color: onBrand }}>{q.name.trim().charAt(0).toUpperCase()}</span> : null;
  const person = (q: QuoteItem, i: number) => (
    <span className="min-w-0 text-sm leading-tight">
      {q.name && <span className={`block font-semibold ${s.heading}`}>{txt(q.name, (t) => patch(i, { name: t }))}</span>}
      {q.role && <span className={`mt-0.5 block ${s.label ?? s.sub}`}>{txt(q.role, (t) => patch(i, { role: t }))}</span>}
    </span>
  );
  const media = (q: QuoteItem) => q.image
    ? (q.video_url
        ? <VideoPoster url={q.video_url} poster={q.image} title={q.name} frame="rounded-xl" accent={btnBg} onAccent={onBtn} disabled={editing} />
        : <img src={q.image} alt="" loading="lazy" decoding="async" className="aspect-video w-full rounded-xl object-cover" />)
    : null;

  const design = normalizeSectionDesign(section.design);
  const gridProps = sectionGridProps(design, k.stack, section.variant === 'list' ? 'review-list' : section.variant === 'wall' ? 'review-wall' : undefined);
  if (section.variant === 'list') {
    return withHead(
      <div className="mx-auto grid max-w-4xl gap-4" {...gridProps}>
        {quotes.map(({ q, i }) => (
          <figure key={i} className={`flex flex-col gap-4 p-6 sm:flex-row sm:items-start sm:gap-7 sm:p-8 ${card}`}>
            {avatar(q, 'h-20 w-20')}
            <div className="grid min-w-0 flex-1 gap-3">
              {!!q.rating && <Stars n={q.rating} color={s.star} />}
              <blockquote className={tz('quote')}>{txt(q.quote, (t) => patch(i, { quote: t }), true)}</blockquote>
              {media(q)}
              {person(q, i)}
            </div>
          </figure>
        ))}
      </div>,
    );
  }

  const wall = section.variant === 'wall';
  const n = quotes.length;
  const cols = n === 1 ? 'max-w-xl' : n === 2 || n === 4 ? 'sm:grid-cols-2 max-w-4xl' : 'sm:grid-cols-2 lg:grid-cols-3 max-w-6xl';
  const items = quotes.map(({ q, i }) => (
    <figure key={i} className={`flex flex-col gap-4 p-6 ${card} ${wall ? 'mb-5 break-inside-avoid' : ''}`}>
      {media(q)}
      {!!q.rating && <Stars n={q.rating} color={s.star} />}
      <blockquote className={`flex-1 ${tz('body')}`}>{txt(q.quote, (t) => patch(i, { quote: t }), true)}</blockquote>
      {(q.name || q.photo_url) && (
        <figcaption className="flex items-center gap-3">
          {/* A card that leads with its own photo or video shows the person
              there, so it skips the avatar (no second, different face). */}
          {!q.image && avatar(q, 'h-10 w-10')}
          {person(q, i)}
        </figcaption>
      )}
    </figure>
  ));
  return withHead(wall
    ? <div className="mx-auto max-w-6xl gap-5 sm:columns-2 lg:columns-3 [column-fill:_balance]" {...gridProps}>{items}</div>
    : <div className={`mx-auto grid grid-cols-1 items-stretch gap-5 ${cols}`} {...gridProps}>{items}</div>);
}
